import React, { useMemo } from "react";

import {
  DefinePlayerDataSet,
  GlobalReportSettings,
  ImportPreviousConversationButton,
  type PlayerDataSet,
  type ReportSettings,
  TestingPanel,
} from "../../..";
import { useAppSelector } from "../../../../redux/hooks";
import { selectOrg } from "../../../../redux/slices/orgSlice";
import { selectPlayers } from "../../../../redux/slices/playerSlice";
import { selectUserPermissions } from "../../../../redux/slices/userSlice";
import { REBOOT_LIVE_TESTING_PERMISSION } from "../../constants";
import { useInitContainer } from "../../hooks";
import { AnalysisSections, type ReportSection } from "./AnalysisSections";
import { generatePrompt } from "./generatePrompt";
import { ReadyToGenerate } from "./ReadyToGenerate";

interface AnalysisBuilderModeProps {
  playerData: PlayerDataSet;
  onPlayerDataChange: React.Dispatch<React.SetStateAction<PlayerDataSet>>;
  settings: ReportSettings;
  onSettingsChange: (settings: ReportSettings) => void;
  sections: ReportSection[];
  onSectionsChange: (sections: ReportSection[]) => void;
  onReset: () => void;
  onGenerate: () => void;
  onOpenImportModal?: () => void;
  /** Bumped by the parent on Reset to remount DefinePlayerDataSet, clearing its internal load/checkbox state */
  resetKey?: number;
}

export const AnalysisBuilderMode: React.FC<AnalysisBuilderModeProps> = ({
  playerData,
  onPlayerDataChange,
  settings,
  onSettingsChange,
  sections,
  onSectionsChange,
  onReset,
  onGenerate,
  onOpenImportModal,
  resetKey = 0,
}) => {
  const players = useAppSelector(selectPlayers);
  const org = useAppSelector(selectOrg);
  const permissions = useAppSelector(selectUserPermissions);
  const canAccessTestingPanel = permissions.includes(
    REBOOT_LIVE_TESTING_PERMISSION,
  );

  // Container warm-up — owned here so the same instance is reused by
  // DefinePlayerDataSet (auto-fire after upload) and TestingPanel (manual
  // re-run with an edited prompt). Stream state is forwarded to TestingPanel
  // so re-runs can show live output. Failures are non-fatal.
  const {
    initContainer,
    isStreaming: isInitStreaming,
    streamedContent: initStreamedContent,
    error: initError,
  } = useInitContainer({
    onComplete: (containerId, summary) => {
      if (!containerId && !summary) return;
      onPlayerDataChange((prev) => ({
        ...prev,
        ...(containerId ? { containerId } : {}),
        ...(summary ? { dataSummary: summary } : {}),
      }));
    },
  });

  // True while the /ai/upload_data request is in flight
  // or while the silent container warm-up that follows it hasn't
  // returned a chat-ready container_id yet — Generate must stay disabled the
  // whole time or the visible chat starts without a reference to the files.
  const isInitializing =
    playerData.isUploading || (playerData.isLoaded && isInitStreaming);

  // Check if player data is loaded and the container warm-up has finished
  const isReadyToGenerate = playerData.isLoaded && !isInitStreaming;

  const movementType = useMemo(
    () =>
      org.details.movementTypes.find(
        (m) => m.id.toString() === playerData.movementTypeId,
      ),
    [org.details.movementTypes, playerData.movementTypeId],
  );

  // Compute the prompt that the form would generate, for use as default in the testing panel
  const generatedPrompt = useMemo(() => {
    const player = players.playerList.find(
      (p) => p.orgPlayerId === playerData.playerId,
    );
    const playerName = player
      ? `${player.firstName} ${player.lastName}`
      : "[PLAYER_NAME]";

    const movementTypeName = movementType
      ? movementType.name
      : "[MOVEMENT_TYPE]";

    return generatePrompt({
      playerData,
      settings,
      sections,
      playerName,
      movementTypeName,
    });
  }, [playerData, settings, sections, players.playerList, movementType]);

  return (
    <div
      className="flex-1 bg-[#0b0b0b] overflow-y-auto"
      data-testid="reboot-live-analysis-builder-mode"
    >
      <div className="max-w-[920px] mx-auto py-8 px-6 pt-20 flex flex-col gap-6">
        {/* Header */}
        <div className="box-border content-stretch flex flex-col gap-[16px] items-center not-italic pb-[30px] pt-0 px-0 relative shrink-0 text-center text-white">
          <div className="flex flex-col font-['Bebas_Neue'] font-normal justify-center relative shrink-0 text-[64px] uppercase leading-[36px]">
            <p data-testid="analysis-builder-title" className="leading-[36px]">
              Analysis Builder
            </p>
          </div>
          <p
            data-testid="analysis-builder-description"
            className="font-normal leading-[23px] relative shrink-0 text-[16px] text-white"
          >
            Create one or more sections. Each section becomes a part of the
            final report.
          </p>
          {onOpenImportModal && (
            <div className="flex flex-col items-center gap-2 pt-2">
              <ImportPreviousConversationButton onClick={onOpenImportModal} />
            </div>
          )}
        </div>

        {/* Define Player Data Set */}
        <DefinePlayerDataSet
          key={resetKey}
          playerData={playerData}
          onPlayerDataChange={onPlayerDataChange}
          initContainer={initContainer}
        />

        {/* Global Report Settings */}
        <GlobalReportSettings
          settings={settings}
          onSettingsChange={onSettingsChange}
          isDisabled={!playerData.hasStartedLoading && !playerData.isLoaded}
        />

        {/* Analysis Sections */}
        <AnalysisSections
          sections={sections}
          onSectionsChange={onSectionsChange}
          isDisabled={!playerData.hasStartedLoading && !playerData.isLoaded}
          movementTypeSlug={movementType?.slug}
        />

        {/* Ready to Generate */}
        <ReadyToGenerate
          isReadyToGenerate={isReadyToGenerate}
          isInitializing={isInitializing}
          onReset={onReset}
          onGenerate={onGenerate}
        />

        {/* Testing Panel - shown after data loads, gated behind reboot_live:testing */}
        {canAccessTestingPanel &&
          playerData.isLoaded &&
          playerData.fileIds.length > 0 && (
            <div className="flex justify-center">
              <TestingPanel
                fileIds={playerData.fileIds}
                dataSummary={playerData.dataSummary}
                defaultReportPrompt={generatedPrompt}
                filesList={playerData.filesList}
                containerId={playerData.containerId}
                initContainer={initContainer}
                initStream={{
                  isStreaming: isInitStreaming,
                  streamedContent: initStreamedContent,
                  error: initError,
                }}
              />
            </div>
          )}
      </div>
    </div>
  );
};
